"use client";

import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { del, fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import {
    COMMAND_SCHEDULER_ENTITY_TYPE,
    COMMAND_SCHEDULER_JOB_NAME,
    COMMAND_SCHEDULER_SESSION_ENTITY_TYPE,
} from "@/components/aios-command/chat/chat-constants";
import { SCHEDULED_JOBS_API_PATH, SCHEDULED_JOB_FILTER_ALL } from "@/components/scheduled-job";
import { DEFAULT_TABLE_PAGE_LIMIT } from "@/utils/global";

export const COMMAND_V2_SCHEDULES_QUERY_KEY = "command-v2-schedules";
export const COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY =
    "command-v2-active-schedule-count";

function isCommandScheduleJob(job: ScheduledJob): boolean {
    const entity = (job.entity_type ?? "").toLowerCase();
    if (entity === COMMAND_SCHEDULER_ENTITY_TYPE || entity === "command_session") {
        return true;
    }
    return (job.job_name ?? "") === COMMAND_SCHEDULER_JOB_NAME;
}

function buildSchedulesUrl(params: {
    search: string;
    page: number;
    limit: number;
    status?: string;
    executionStatus?: string;
    entityId?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
}): string {
    const query = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        sort_by: params.sortBy ?? "created_at",
        sort_order: params.sortOrder ?? "desc",
    });
    const entityId =
        params.entityId && params.entityId !== SCHEDULED_JOB_FILTER_ALL ? params.entityId : "";
    if (entityId) {
        query.append("entity_type", COMMAND_SCHEDULER_ENTITY_TYPE);
        query.append("entity_id", entityId);
    } else {
        query.append("entity_type", COMMAND_SCHEDULER_ENTITY_TYPE);
        query.append("entity_type", COMMAND_SCHEDULER_SESSION_ENTITY_TYPE);
    }
    if (params.status && params.status !== SCHEDULED_JOB_FILTER_ALL) {
        query.set("status", params.status);
    }
    if (params.executionStatus && params.executionStatus !== SCHEDULED_JOB_FILTER_ALL) {
        query.append("execution_status", params.executionStatus);
    }
    if (params.search.trim()) query.set("search", params.search.trim());
    return `${SCHEDULED_JOBS_API_PATH}?${query.toString()}`;
}

interface UseCommandV2SchedulesOptions {
    search?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
    countOnly?: boolean;
    executionStatus?: string;
    jobStatus?: string;
    entityId?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
}

export function useCommandV2Schedules(options?: UseCommandV2SchedulesOptions) {
    const search = options?.search?.trim() ?? "";
    const page = options?.page ?? 1;
    const limit = options?.limit ?? DEFAULT_TABLE_PAGE_LIMIT;
    const executionStatus = options?.executionStatus ?? SCHEDULED_JOB_FILTER_ALL;
    const jobStatus = options?.jobStatus ?? SCHEDULED_JOB_FILTER_ALL;
    const entityId = options?.entityId ?? SCHEDULED_JOB_FILTER_ALL;
    const sortBy = options?.sortBy ?? "created_at";
    const sortOrder = options?.sortOrder ?? "desc";
    const enabled = options?.enabled !== false;
    const listEnabled = enabled && !options?.countOnly;
    const queryClient = useQueryClient();

    const listQuery = useProtectedQuery<ScheduledJobsListResponse>({
        queryKey: [
            COMMAND_V2_SCHEDULES_QUERY_KEY,
            search,
            page,
            limit,
            executionStatus,
            jobStatus,
            entityId,
            sortBy,
            sortOrder,
        ],
        queryFn: () =>
            fetchData(
                buildSchedulesUrl({
                    search,
                    page,
                    limit,
                    executionStatus,
                    status: jobStatus,
                    entityId,
                    sortBy,
                    sortOrder,
                })
            ),
        enabled: listEnabled,
        requiredPermissions: [],
    });

    const countQuery = useProtectedQuery<ScheduledJobsListResponse>({
        queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY],
        queryFn: () =>
            fetchData(
                buildSchedulesUrl({
                    search: "",
                    page: 1,
                    limit: 1,
                    status: "active",
                })
            ),
        enabled,
        requiredPermissions: [],
        staleTime: 30_000,
    });

    const jobs = useMemo(() => {
        const items = listQuery.data?.items ?? [];
        return items.filter((job) => isCommandScheduleJob(job));
    }, [listQuery.data]);

    const totalCount = listQuery.data?.total_count ?? jobs.length;
    const activeCount = countQuery.data?.total_count ?? 0;

    const deleteJob = useProtectedMutation({
        mutationFn: (jobId: string) => del(`${SCHEDULED_JOBS_API_PATH}/${jobId}`),
        requiredPermissions: [],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [COMMAND_V2_SCHEDULES_QUERY_KEY] });
            queryClient.invalidateQueries({
                queryKey: [COMMAND_V2_ACTIVE_SCHEDULE_COUNT_QUERY_KEY],
            });
            queryClient.invalidateQueries({ queryKey: ["scheduled-jobs"] });
            toast.success("Schedule deleted", {
                description: "This prompt has been removed and will not run again.",
            });
        },
        onError: (error: unknown) => {
            const message =
                (error as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ?? "Please try again.";
            toast.error("Failed to delete schedule", { description: message });
        },
    });

    return {
        jobs,
        activeCount,
        totalCount,
        pageCount: listQuery.data?.page_count ?? 0,
        page,
        limit,
        isLoading: listQuery.isLoading,
        isFetching: listQuery.isFetching,
        deleteJob: deleteJob.mutate,
        isDeleting: deleteJob.isPending,
    };
}
